// Copyright 2016 Sorama B.V.

import { autoinject, computedFrom, Disposable, transient } from "aurelia-framework";
import { Router } from "aurelia-router";
import { BusinessUnitDTO, RepresentativeDTO } from "datatypes/SoramaWebApi";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { AccountRepository } from "repositories/AccountRepository";
import { BusinessUnitRepository } from "repositories/BusinessUnitRepository";
import { RepresentativeRepository } from "repositories/RepresentativeRepository";
import { StorageRepository } from "repositories/StorageRepository";
import { ILicenseService } from "services/License/ILicenseService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

@autoinject
@transient()
export class UserInformationModule implements IWorkflowModuleLifetime {
    //#region UI Properties
    public bytesUsedInCloud: number;
    public passwordResetSend = false;
    public passwordRequestInProgress = false;
    private moduleInstance: IWorkflowModuleInstance;

    @computedFrom("licenseService.activeMembershipLicense", "licenseService.activeMembershipLicenseLoaded")
    public get validLicense(): boolean {
        return !!this.licenseService.activeMembershipLicense || !this.licenseService.activeMembershipLicenseLoaded;
    }

    @computedFrom("serverStatusService.status")
    public get isOffline(): boolean {
        return this.serverStatusService.status != ServerStatus.Online;
    }

    public representative: RepresentativeDTO;
    public businessUnit: BusinessUnitDTO;
    //#endregion

    //#region Private properties

    private disposables: Disposable[] = [];

    //#endregion

    //#region Constructor and Aurelia Functions
    constructor(
        private readonly authentication: IAuthentication,
        private readonly serverStatusService: IServerStatusService,
        private readonly storageRepository: StorageRepository,
        private readonly licenseService: ILicenseService,
        private readonly representativeRepository: RepresentativeRepository,
        private readonly businessUnitRepository: BusinessUnitRepository,
        private readonly accountRepository: AccountRepository,
        private readonly shellviewManager: ShellViewManager,
        private readonly workflowModeService: WorkflowModeService,
        private readonly router: Router
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        // Get the total storage for this user (no need to wait for it)
        this.storageRepository
            .getStorageSize()
            .then((x) => {
                this.bytesUsedInCloud = x;
            })
            .catch((e) => {
                console.error(e);
            });
        this.representativeRepository
            .getRepresentative(this.authentication.userId)
            .then((x) => (this.representative = x))
            .catch((e) => {
                console.error(e);
            });
        this.businessUnitRepository
            .getBusinessUnit(this.authentication.businessUnitId)
            .then((bu) => (this.businessUnit = bu))
            .catch((e) => {
                console.error(e);
            });
    }

    public deactivateModule(): void {
        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }
    }

    //#endregion

    // #region User Input Methods
    public resetPassword() {
        this.passwordRequestInProgress = true;
        this.accountRepository
            .resetPasswordRequest()
            .then(() => {
                this.passwordResetSend = true;
            })
            .catch(() => {
                this.moduleInstance
                    .createNotification()
                    .withMessage("Unable to reset password")
                    .withUrgency(NotificationUrgency.Warning)
                    .show();
            })
            .then(() => {
                this.passwordRequestInProgress = false;
            });
    }

    public signOut(): void {
        // Start by closing the sidepanel
        this.shellviewManager.closeRightSideBar();

        // Sign out
        this.authentication.signOut();
    }

    public navigateToWorkflowModeSelection(): void {
        this.router.navigate("/WorkflowModeSelection");
    }
    //#endregion
}
